import React, { useMemo, useRef, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import {
  PageContainer,
  DataTableContainer,
  ViewGuard,
  TeamTransferConfirmModal,
} from '../index';
import {
  usePermissionsContext,
  useAuth,
  SCREENS,
} from '../../hooks';
import AddIcon from '@mui/icons-material/Add';
import {
  TABLE_COLUMNS,
  FILTER_CONFIGS,
  SEARCH_CONFIGS,
  FORM_CONFIGS,
} from '../../constants';
import { buildHasuraWhere } from '../../utils/buildHasuraWhere';

// screen=null verilirse permission gate tamamen bypass edilir (sayfa kendi
// kisitini yonetiyor demektir — orn. DocumentBank server-side row filter +
// role-based row action gating). Bu durumda ViewGuard sarilmaz, tum action'lar
// varsayilan olarak izinli sayilir.
const OPEN_PERMISSIONS = {
  canView: true, canCreate: true, canEdit: true, canDelete: true, canDownload: true,
};

const DynamicCrudPage = ({
  entityType,
  screen = SCREENS.SETTINGS,
  hook,
  hookConfig,
  additionalHooks = {},
  customFormFields,
  customFilterConfigs,
  customSearchConfigs,
  customTitle,
  addButtonTextKey,
  addButtonIcon = <AddIcon />,
  entityTypeForDataTable,
  customTransformEditData,
  customTransformFormData,
  showAddButton = true,
  customRowActions,
  onRowAction,
  deleteConfirmText,
  hideDefaultActions = false,
  exportButton, // undefined = use permission check
  onExport,
  exportDisabled = false,
  exportLabel = 'table.actions.exportCsv',
  // Header'da (indir/ekle butonlarının solunda) ekstra custom buton/içerik
  // basmak için. Bulk-edit gibi sayfa özel aksiyonları toolbar içine almak
  // istediğimizde kullanılır.
  headerExtraContent = null,
  commentEntityType = null,
  getRowSx,
  // Optional gate that runs BEFORE `handleFormSubmit`. If it returns a
  // falsy value (or throws), submission is aborted — pages can plug in
  // confirm dialogs for edge cases (e.g. payment schedule mismatch) without
  // wrapping DynamicCrudPage. Receives `(formData, isEditMode, currentEditData)`.
  beforeSubmit,
}) => {
  const { t } = useTranslation();
  const { user: currentUser } = useAuth();
  const { getScreenPermissions } = usePermissionsContext();
  const screenPermissions = screen ? getScreenPermissions(screen) : OPEN_PERMISSIONS;
  const dataTableRef = useRef();

  // List page: enable backend pagination on the main entity hook. Additional
  // hooks (form dropdowns) are called WITHOUT { paginated: true } so their
  // queries return all rows — otherwise selects would only show the first 20.
  const hookData = hookConfig
    ? hook(hookConfig())
    : hook({ paginated: true });

  const additionalData = {};
  Object.entries(additionalHooks).forEach(([key, hookFn]) => {
    try {
    additionalData[key] = hookFn();
    } catch (error) {
      console.warn(`Error in additional hook ${key}:`, error);
      additionalData[key] = {};
    }
  });

  const {
    [entityType]: data,
    isLoading,
    isCreating,
    isUpdating,
    isDeleting,
    isRootUser,
    handleFormSubmit,
    [`delete${entityType.charAt(0).toUpperCase() + entityType.slice(1)}`]: deleteFunction,
    deleteTeamMember,
    additionalData: hookAdditionalData,
    // Transfer modal states
    transferModalOpen,
    transferData,
    isTransferring,
    closeTransferModal,
    transferUserToTeam,
    // Backend pagination — forwarded to DataTableContainer. When
    // serverPagination is false, the DataTable falls back to its
    // existing client-side pagination logic.
    serverPagination,
    page,
    pageSize,
    totalCount,
    hasNextPage,
    setPage,
    setPageSize,
    // Backend filter/search — only present when the entity hook routes
    // through useDynamicEntity. When serverPagination is on, we translate
    // the DataTable's filter/search UI state into a Hasura `_bool_exp` and
    // push it into the hook so the next fetch covers the whole dataset.
    setExtraWhereCondition,
    fetchAllForExport,
  } = hookData;

  const mergedAdditionalData = {
    ...additionalData,
    ...hookAdditionalData,
  };

  const formFields = useMemo(() => {
    let fields = FORM_CONFIGS[entityType]?.fields || [];

    if (customFormFields) {
      fields = customFormFields(fields, mergedAdditionalData, isRootUser, t, {});
    } else {
      fields = fields.map(field => {
        if (field.name === 'companyId' && mergedAdditionalData.companies) {
          return {
            ...field,
            options: mergedAdditionalData.companies.companies?.map(c => ({ value: c.id, label: c.name })) || 
                     mergedAdditionalData.companies.map(c => ({ value: c.id, label: c.name })) || [],
            showIf: () => isRootUser,
          };
        }
        return field;
      }).filter(field => (field.name === 'companyId' ? isRootUser : true));
    }

    return fields;
  }, [entityType, mergedAdditionalData, isRootUser, customFormFields, t]);

  const filterConfigs = useMemo(() => {
    let filters = FILTER_CONFIGS[entityType] || [];

    if (customFilterConfigs) {
      filters = customFilterConfigs(filters, mergedAdditionalData, isRootUser, t);
    } else {
      filters = filters
        .filter(f => (f.showIfRoot ? isRootUser : true))
        .map(f => {
          if (f.field === 'company_name' && data) {
            const uniqueCompanies = [
              ...new Set(data.map(item => item.company_name)),
            ].filter(Boolean);
            return {
              ...f,
              options: uniqueCompanies.map(name => ({
                value: name,
                label: name,
              })),
            };
          }
          if (f.field === 'status' && f.options) {
            return {
              ...f,
              options: f.options.map(opt => ({
                value: opt.value,
                label: t(opt.labelKey),
              })),
            };
          }
          return f;
        });
    }

    return filters;
  }, [entityType, data, mergedAdditionalData, isRootUser, customFilterConfigs, t]);

  const searchConfig = useMemo(() => {
    let config = SEARCH_CONFIGS[entityType] || {};

    if (customSearchConfigs) {
      config = customSearchConfigs(config, t);
    }

    return config;
  }, [entityType, customSearchConfigs, t]);

  const pageConfig = useMemo(
    () => ({
      // `hiddenFromTable: true` keeps the column in TABLE_COLUMNS (so the
      // data is still loaded/transformed) but prevents it from rendering as
      // a visible table column. Search/filter configs reference fields by
      // name independently, so filtering is unaffected.
      columns: (TABLE_COLUMNS[entityType] || [])
        .filter(col => (col.showIfRoot ? isRootUser : true))
        .filter(col => !col.hiddenFromTable),
      filterConfigs,
      searchConfig,
      formConfig: {
        ...FORM_CONFIGS[entityType],
        fields: formFields
      },
    }),
    [entityType, filterConfigs, searchConfig, formFields, isRootUser]
  );

  const rowActions = useMemo(() => {
    // If user doesn't have view permission, show no actions
    if (!screenPermissions.canView) {
      return [];
    }

    if (customRowActions) {
      return (row) => {
        const actions = customRowActions(row);
        
        // If hideDefaultActions is true, only return custom actions
        if (hideDefaultActions) {
          return actions;
        }
        
        return actions.filter(action => {
          if (action.type === 'edit') {
            return screenPermissions.canEdit;
          }
          if (action.type === 'delete' || action.type === 'remove') {
            return screenPermissions.canDelete;
          }
          if (action.type === 'members' || action.type === 'navigate') {
            return screenPermissions.canView;
          }
          return true;
        });
      };
    }

    // If hideDefaultActions is true and no custom actions, return empty array
    if (hideDefaultActions) {
      return [];
    }

    const actions = [];

    if (screenPermissions.canEdit) {
      actions.push({
        type: 'edit',
        label: 'table.actions.edit',
        color: 'primary',
      });
    }
    if (screenPermissions.canDelete) {
      actions.push({
        type: 'delete',
        label: 'table.actions.delete',
        color: 'error',
      });
    }

    return actions;
  }, [customRowActions, screenPermissions, t, screen, hideDefaultActions]);

  const canCreate = screenPermissions.canCreate && showAddButton;
  const showExportButton = exportButton !== undefined ? exportButton : screenPermissions.canDownload;

  const handleFormSubmitWrapper = async (
    formData,
    isEditMode,
    currentEditData
  ) => {
    if (beforeSubmit) {
      try {
        const proceed = await beforeSubmit(formData, isEditMode, currentEditData);
        if (!proceed) {
          // Signal failure without triggering the DataTableContainer's
          // "modal close on success" branch — the form stays open.
          return false;
        }
      } catch (err) {
        console.error('beforeSubmit gate failed:', err);
        return false;
      }
    }

    let transformedData = formData;

    if (customTransformFormData) {
      transformedData = customTransformFormData(formData);
    }

    const id = isEditMode && currentEditData?.id ? currentEditData.id : null;
    return await handleFormSubmit(transformedData, id);
  };

  const handleRowAction = (actionType, row) => {
    if (onRowAction) {
      return onRowAction(actionType, row);
    }
    return false;
  };

  const handleDelete = async row => {
    if (entityType === 'teamMembers' && deleteTeamMember) {
      return await deleteTeamMember(row.id);
    }
    return await deleteFunction(row.id);
  };

  const getTitle = () => {
    if (customTitle) {
      const baseTitle = customTitle();
      if (entityType === 'teamMembers' && hookData.team?.name) {
        return `${hookData.team.name} - ${baseTitle}`;
      }
      return baseTitle;
    }
    if (screen === SCREENS.SETTINGS) {
      return t(`settings.titles.${entityType}`);
    }
    return t(`pages.${entityType}`);
  };

  const getCurrentPage = () => {
    if (screen === SCREENS.SETTINGS) {
      return t(`pages.${entityType}`);
    }
    return t(`pages.${entityType}`);
  };

  // Backend-side filter+search bridge. The DataTableContainer fires this
  // whenever its filter UI or search input changes; we translate it into a
  // Hasura `_bool_exp` and push it into the entity hook. The hook merges it
  // with the permission-driven `customWhereCondition` and resets to page 1
  // (via JSON.stringify dep tracking inside useDynamicEntity).
  const handleFiltersChange = useCallback(
    (uiFilters, uiSearch) => {
      if (!setExtraWhereCondition) return;
      const where = buildHasuraWhere({
        filters: uiFilters,
        searchValue: uiSearch,
        searchFields: pageConfig.searchConfig.fields || [],
        filterConfigs: pageConfig.filterConfigs || [],
      });
      setExtraWhereCondition(
        Object.keys(where).length > 0 ? where : null
      );
    },
    [setExtraWhereCondition, pageConfig.searchConfig.fields, pageConfig.filterConfigs]
  );

  // When server pagination is off, make sure no stale extra where lingers.
  useEffect(() => {
    if (!serverPagination && setExtraWhereCondition) {
      setExtraWhereCondition(null);
    }
  }, [serverPagination, setExtraWhereCondition]);

  // screen==null → ViewGuard'i tamamen atla (sayfa kendi yetkilendirmesini
  // yonetiyor). Aksi halde default davranis: SCREENS.<X> uzerinden VIEW check.
  // NOT: Guard'i inline component olarak render icinde OLUSTURMAYIZ — her render
  // yeni bir tip yaratir, React alt agaci komple unmount+remount eder, boylece
  // SearchBar gibi local state'i olan alt bilesenler her tuslamada sifirlanir.
  const inner = (
    <PageContainer currentPage={getCurrentPage()}>
        <DataTableContainer
          ref={dataTableRef}
          title={getTitle()}
          columns={pageConfig.columns}
          data={data || []}
          formFields={pageConfig.formConfig.fields}
          onFormSubmit={handleFormSubmitWrapper}
          addModalTitle={pageConfig.formConfig.title?.add}
          editModalTitle={pageConfig.formConfig.title?.edit}
          showSearch={true}
          showFilters={pageConfig.filterConfigs.length > 0}
          showPagination={true}
          showAddButton={canCreate}
          addButtonText={t(addButtonTextKey)}
          addButtonIcon={addButtonIcon}
          onDelete={handleDelete}
          filterConfigs={pageConfig.filterConfigs}
          searchPlaceholder={pageConfig.searchConfig.placeholder}
          searchFields={pageConfig.searchConfig.fields}
          enableAutoFilter={true}
          rowsPerPage={serverPagination ? pageSize : 20}
          // Backend-paginated fields — ignored by DataTableContainer when
          // serverPagination is false.
          serverPagination={serverPagination}
          serverPage={page}
          serverPageSize={pageSize}
          serverTotalCount={totalCount}
          serverHasNextPage={hasNextPage}
          onServerPageChange={setPage}
          onServerPageSizeChange={setPageSize}
          // Backend filter/search opt-in. We piggyback on serverPagination
          // because pages without server pagination still load the full
          // dataset and benefit from client-side filtering (avoids extra
          // round-trips and keeps display-name/transformed-field filters
          // working without per-config Hasura mappings).
          serverFilters={!!serverPagination && !!setExtraWhereCondition}
          onFiltersChange={handleFiltersChange}
          rowActions={rowActions}
          onRowAction={handleRowAction}
          entityType={entityTypeForDataTable || entityType}
          loading={isLoading}
          submitting={isCreating || isUpdating}
          deleting={isDeleting}
          currentUser={currentUser}
          transformEditData={customTransformEditData}
          deleteConfirmText={deleteConfirmText}
          exportButton={showExportButton}
          onExport={onExport}
          headerExtraContent={headerExtraContent}
          onFetchAllForExport={fetchAllForExport}
          exportDisabled={exportDisabled}
          exportLabel={exportLabel}
          commentEntityType={commentEntityType}
          getRowSx={getRowSx}
        />
        
        {/* Transfer Modal */}
        {transferModalOpen && transferData && (
          <TeamTransferConfirmModal
            open={transferModalOpen}
            onClose={closeTransferModal}
            onConfirm={async () => {
              const success = await transferUserToTeam();
              if (success) {
                if (dataTableRef.current && dataTableRef.current.handleModalClose) {
                  dataTableRef.current.handleModalClose();
                }
              }
            }}
            user={transferData.user}
            currentTeam={transferData.currentTeam}
            newTeam={transferData.newTeam}
            loading={isTransferring}
          />
        )}
    </PageContainer>
  );

  return screen ? <ViewGuard screen={screen}>{inner}</ViewGuard> : inner;
};

export default DynamicCrudPage; 